import { or } from 'tio-ui/utilities';
import Chip from 'tio-ui/components/chip';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import {
  PhArrowSquareUpRight,
  PhCompass,
  PhClock,
  PhCreditCard,
  PhLock,
} from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import type { IntlService } from 'ember-intl';
import type { TasProgramInstance as TasProgramInstanceResult } from 'tio-data/data/resources/search.ext';
import WhosViewingComponent from '../../tio/whos-viewing.gts';
import type { Appearance } from '../../../services/appearance.ts';
import type { TOC } from '@ember/component/template-only';

// Co-located outline "view" button — the queue row renders two of these
// (new application view, new program view) that differ only by
// route / model / query / label / test attribute. One component, two calls.
interface ViewButtonSignature {
  Args: {
    route: string;
    model?: string;
    query?: Record<string, unknown>;
    label: string;
  };
  Element: HTMLAnchorElement;
}

const ViewButton: TOC<ViewButtonSignature> = <template>
  <LinkTo
    @route={{@route}}
    @model={{@model}}
    @query={{if @query @query (hash)}}
    class="app-btn-outline"
    ...attributes
  >
    {{@label}}
  </LinkTo>
</template>;

interface ApplicationRowSignature {
  Args: {
    application: TasProgramInstanceResult;
    personRoute: string;
    /** Override the model ID passed to personRoute. Defaults to application.personId. */
    personModel?: number | string;
    rootRoute: string;
    /** Marks an admin queue row as read-only for the current reviewer. */
    readOnly?: boolean;
    viewers?: Appearance[] | Record<string, Appearance>;
  };
  Element: HTMLDivElement;
}

const priorityBarColor = (dateString?: string) => {
  if (!dateString) return 'tio-priority-bar--unknown';

  const date = new Date(dateString);
  const now = new Date();
  const daysAgo = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (daysAgo > 5) return 'tio-bg-danger';
  if (daysAgo >= 3) return 'tio-bg-warning';
  return 'tio-bg-success';
};

export default class ApplicationRow extends Component<ApplicationRowSignature> {
  @service declare intl: IntlService;

  // Preserve array and keyed-object payloads for downstream normalization.
  get appearances(): Appearance[] | Record<string, Appearance> {
    return this.args.viewers || [];
  }

  get viewerCount(): number {
    if (Array.isArray(this.appearances)) {
      return this.appearances.length;
    }

    return Object.keys(this.appearances).length;
  }

  get isAssetReview(): boolean {
    return this.args.application.recordType === 'asset';
  }

  get isCourseCash(): boolean {
    return (
      this.isAssetReview ||
      (this.args.application.tasApplicationCourseCashReservedAmountInteger ?? 0) > 0 ||
      (this.args.application.tasApplicationCourseCashUsedAmountInteger ?? 0) > 0
    );
  }

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ApplicationState.UPDATED_ON');
  };

  get originalSubmissionTs(): string | undefined {
    const application = this.args.application;

    if (this.isAssetReview) {
      return application.attentionPriorityDate;
    }

    return (
      application.tasApplicationAttentionPriorityDate ||
      application.tasProgramInstanceAttentionPriorityDate ||
      application.tasApplicationLatestSubmissionTs ||
      application.tasProgramInstanceLatestSubmissionTs
    );
  }

  // Show the new program approval view button when the row is a program-only
  // row (no application).
  get showNewProgramViewButton(): boolean {
    if (this.args.application.tasApplicationIdKeyword) return false;
    return true;
  }

  get applicationViewQuery(): Record<string, unknown> {
    return this.isAssetReview ? { courseCash: true } : {};
  }

  <template>
    <div class="tio-approval-queue-row tio-row-start-between app-card p-4 transition relative">
      {{! Left vertical red bar }}
      <div
        class="absolute left-0 top-0 bottom-0 w-1 rounded-l
          {{priorityBarColor
            (or
              @application.tasProgramInstanceAttentionPriorityDate
              @application.attentionPriorityDate
            )
          }}"
      />
      {{! Main Content }}
      <div class="pl-4 w-full">
        <div class="tio-row-between">
          <div>
            <h3 class="font-semibold tio-fg-primary flex gap-x-4">
              {{#if this.isAssetReview}}
                <span>{{t "common.tas_approval_queue.assistance_review"}}</span>
                <Chip @intent="warning" class="uppercase">
                  {{t "common.tas_approval_queue.card_usage_review"}}
                </Chip>
                <PhCreditCard @size={{16}} class="app-icon-warning" />
              {{else}}
                <span>{{t "common.tas_approval_queue.application_number" id=@application.id}}</span>
                {{#if @application.tasApplicationIdKeyword}}
                  <Chip class="uppercase">{{t "common.tas_approval_queue.course"}}</Chip>
                {{else}}
                  <Chip class="uppercase">{{t "common.tas_approval_queue.program"}}</Chip>
                {{/if}}
                {{#if this.isCourseCash}}
                  <PhCreditCard @size={{16}} class="app-icon-warning" />
                {{/if}}
              {{/if}}
              {{#if @readOnly}}
                <Chip
                  @intent="default"
                  @appearance="faded"
                  @size="sm"
                  data-test-approval-queue-read-only-chip
                >
                  <span class="tio-row-extra-tight">
                    <PhLock @size={{14}} />
                    {{t "common.tas_approval_queue.read_only"}}
                  </span>
                </Chip>
              {{/if}}
            </h3>
            <p class="text-sm tio-fg-muted">
              <LinkTo
                @route={{@personRoute}}
                @model={{if @personModel @personModel @application.personId}}
              >
                <div class="tio-row-flush flex-nowrap">
                  {{@application.employeeNameKeyword}}
                  ({{@application.employeePayrollIdKeyword}}) &bull;
                  {{@application.companyName}}
                  <PhArrowSquareUpRight @size="16" />
                </div>
              </LinkTo>
            </p>
          </div>

          <div class="tio-row-tight">
            {{#if this.viewerCount}}
              <WhosViewingComponent @appearances={{this.appearances}} />
            {{/if}}
            {{#if @application.tasApplicationIdKeyword}}
              <ViewButton
                @route={{concat @rootRoute ".tas.applications.approval"}}
                @model={{@application.tasApplicationIdKeyword}}
                @query={{this.applicationViewQuery}}
                @label={{t "common.tas_approval_queue.view_application"}}
                data-test-new-approval-view-button
              />
            {{/if}}

            {{#if this.showNewProgramViewButton}}
              <ViewButton
                @route={{concat @rootRoute ".tas.programs"}}
                @model={{@application.tasProgramInstanceIdKeyword}}
                @label={{t "common.tas_approval_queue.view_application"}}
                data-test-new-program-view-button
              />
            {{/if}}
          </div>
        </div>

        {{#if @readOnly}}
          <div
            class="tio-row-top mt-3 rounded-md border tio-border-subtle tio-bg-sunken px-3 py-2 tio-text-sm-normal tio-fg-secondary"
            data-test-approval-queue-read-only-message
          >
            <PhLock @size={{16}} class="mt-0.5 shrink-0 tio-fg-muted" />
            <span>{{t "common.tas_approval_queue.read_only_body"}}</span>
          </div>
        {{/if}}

        <div class="mt-3 grid grid-cols-4 gap-y-1 gap-x-4 text-sm tio-fg-secondary">
          <div class="tio-row-extra-tight col-span-2">
            <PhCompass @size={{16}} class="tio-fg-disabled" />
            <span class="font-medium">{{t "common.status"}}</span>
            {{#if this.isAssetReview}}
              {{t "common.tas_approval_queue.pending_assistance_review"}}
            {{else if @application.tasApplicationStateKeyword}}
              {{this.statusText @application.tasApplicationStateKeyword}}
            {{else if @application.tasProgramInstanceStateKeyword}}
              {{this.statusText @application.tasProgramInstanceStateKeyword}}
            {{/if}}
          </div>
          <div class="tio-row-extra-tight">
            <PhClock class="h-4 w-4 tio-fg-disabled" />
            <span class="font-medium">{{t "common.tas_approval_queue.last_action"}}</span>
            {{safeFormatDate
              (or
                @application.tasApplicationLatestStateChangeAtDate
                @application.tasProgramInstanceLatestStateChangeAtDate
                @application.tasAssetUpdatedAtDate
              )
              month="short"
              day="2-digit"
              year="numeric"
            }}
          </div>

          <div class="tio-row-end gap-1">
            {{#if this.originalSubmissionTs}}
              <span
                class="font-medium"
                title={{safeFormatDate
                  this.originalSubmissionTs
                  month="short"
                  day="2-digit"
                  year="numeric"
                }}
              >
                {{t
                  "common.tas_approval_queue.submitted"
                  time=(safeFormatDate
                    this.originalSubmissionTs month="short" day="2-digit" year="numeric"
                  )
                }}
              </span>
            {{else}}
              <span class="font-medium">{{t "common.tas_approval_queue.submitted" time="—"}}</span>
            {{/if}}
          </div>
        </div>
      </div>
    </div>
  </template>
}
